import { getApiV1Url } from '../config/api';
import { getUserInfo } from './auth';

/** 프로젝트 설정 한 항목 (configId → 값, 조회 응답용) */
export interface ProjectConfigItem {
  configId: string;
  value: string[] | string;
}

/** 프로젝트 설정 전체 조회 응답 */
export interface ProjectConfigResponse {
  projectCode: string;
  items: ProjectConfigItem[];
}

/** 프로필 한 건 (저장 요청 body의 profiles[] 요소). category·settingKey·type은 ProjectConfig.tsx의 CATEGORIES[].code, keys[].key, keys[].type과 동일 */
export interface ProfileItem {
  category: string;
  settingKey: string;
  settingValue: {
    type: string;
    value: string[] | string;
  };
}

/** API 조회 응답의 프로필 한 건 (data.profiles[] 요소) */
interface ProfileResponseItem {
  category: string;
  settingKey: string;
  settingValue: { type: string; value: string[] | string };
  projectCode?: string;
  [key: string]: unknown;
}

/**
 * 프로젝트 코드로 해당 프로젝트의 설정 전체 조회
 * API 응답: { data: { profiles: [{ category, settingKey, settingValue: { type, value } }, ...] } }
 */
export async function getProjectConfig(projectCode: string): Promise<ProjectConfigResponse> {
  const userInfo = getUserInfo();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(userInfo && {
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    }),
  };
  const url = getApiV1Url(`/projects/${projectCode}/profiles`);
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    throw new Error(`프로젝트 설정 조회 실패: ${response.status}`);
  }
  const json = await response.json();
  const data = json.data ?? json;
  const profiles: ProfileResponseItem[] = data.profiles ?? [];
  const items: ProjectConfigItem[] = profiles.map((p) => {
    let val = p.settingValue?.value ?? '';
    if (p.settingValue?.type === 'ENUM_SINGLE' && Array.isArray(val)) {
      val = val.length ? val[0] : '';
    }
    return {
      configId: `${p.category}_${p.settingKey}`,
      value: val,
    };
  });
  const resProjectCode = data.projectCode ?? profiles[0]?.projectCode ?? projectCode;
  return { projectCode: resProjectCode, items };
}

/** ProjectConfig UI getConfigId(category, settingKey) 와 동일 */
export const PROJECT_FEATURE_CONFIG_ID = 'PROJECT_FEATURE';
export const PROJECT_EARN_METHOD_CONFIG_ID = 'PROJECT_EARN_METHOD';
export const PROJECT_DEFAULT_BRAND_CONFIG_ID = 'DEFAULT_BRAND';

/** selectedProject(localStorage)에 캐시하는 프로젝트 설정 요약 */
export interface SelectedProjectProfileSettings {
  featureCodes: string[];
  earnMethod?: string;
  defaultBrandCode?: string;
}

function getConfigItemValue(
  config: ProjectConfigResponse,
  configId: string
): string[] | string | undefined {
  return config.items.find((i) => i.configId === configId)?.value;
}

export function parseEnumMultiValue(value: string[] | string | undefined): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value];
  }
  return [];
}

export function parseEnumSingleValue(value: string[] | string | undefined): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value) && value.length > 0) {
    return String(value[0]).trim();
  }
  return '';
}

export function parseDefaultBrandCode(value: string[] | string | undefined): string {
  return parseEnumSingleValue(value);
}

export function orderProjectFeatureCodes(codes: string[]): string[] {
  const order = new Map(PROJECT_FEATURE_OPTIONS.map((o, i) => [o.value, i]));
  return [...new Set(codes)].sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999));
}

export function extractSelectedProjectProfileSettings(
  config: ProjectConfigResponse
): SelectedProjectProfileSettings {
  const featureCodes = orderProjectFeatureCodes(
    parseEnumMultiValue(
      getConfigItemValue(config, PROJECT_FEATURE_CONFIG_ID) as string[] | string | undefined
    )
  );
  const earnMethod = parseEnumSingleValue(
    getConfigItemValue(config, PROJECT_EARN_METHOD_CONFIG_ID) as string[] | string | undefined
  );
  const defaultBrandCode = parseDefaultBrandCode(
    getConfigItemValue(config, PROJECT_DEFAULT_BRAND_CONFIG_ID) as string[] | string | undefined
  );

  return {
    featureCodes,
    ...(earnMethod ? { earnMethod } : {}),
    ...(defaultBrandCode ? { defaultBrandCode } : {}),
  };
}

export function getDefaultBrandCodeFromConfig(config: ProjectConfigResponse): string {
  return parseDefaultBrandCode(
    getConfigItemValue(config, PROJECT_DEFAULT_BRAND_CONFIG_ID) as string[] | string | undefined
  );
}

/** getProjectConfig 1회 → 사용기능·적립방식·기본브랜드 추출 (목록 카드·프로젝트 선택 공용) */
export async function fetchSelectedProjectProfileSettings(
  projectCode: string
): Promise<SelectedProjectProfileSettings> {
  const config = await getProjectConfig(projectCode);
  return extractSelectedProjectProfileSettings(config);
}

/** 프로젝트 선택 시 localStorage용 — profiles API 1회 조회 후 설정 병합 */
export async function attachProjectProfileSettingsToSnapshot<T extends { code: string }>(
  project: T
): Promise<T & SelectedProjectProfileSettings> {
  try {
    const settings = await fetchSelectedProjectProfileSettings(project.code);
    return { ...project, ...settings };
  } catch (err) {
    console.warn('프로젝트 프로필 설정 조회 실패:', project.code, err);
    return { ...project, featureCodes: [] };
  }
}

/** @deprecated attachProjectProfileSettingsToSnapshot 사용 */
export const attachProjectDefaultsToSnapshot = attachProjectProfileSettingsToSnapshot;

/**
 * 프로젝트 프로필 일괄 저장 (변경된 항목만 전송)
 * @param projectCode - 프로젝트 코드
 * @param profiles - 저장할 프로필 목록
 */
export async function updateProjectProfiles(
  projectCode: string,
  profiles: ProfileItem[]
): Promise<void> {
  const userInfo = getUserInfo();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(userInfo && {
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    }),
  };
  const url = getApiV1Url(`/projects/${projectCode}/profiles/bulk`);
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ profiles }),
  });
  if (!response.ok) {
    throw new Error(`프로젝트 설정 저장 실패: ${response.status}`);
  }
}

/** PROJECT / FEATURE ENUM_MULTI 옵션 (ProjectConfig의 OPTION_SETS.FEATURE와 동일) */
export const PROJECT_FEATURE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'SHOWCASE', label: '메뉴 소개' },
  { value: 'MEMBERSHIP', label: '멤버십' },
  { value: 'MOBILE_ORDER', label: '모바일 오더' },
  { value: 'MALL', label: '쇼핑몰' },
];

const PROJECT_FEATURE_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  PROJECT_FEATURE_OPTIONS.map((o) => [o.value, o.label])
);

export function getProjectFeatureLabel(value: string): string {
  return PROJECT_FEATURE_LABEL_BY_VALUE[value] ?? value;
}

/**
 * GET /api/v1/projects/{projectCode}/profiles/{category}/{settingKey}
 * 설정값(ENUM_MULTI 등)의 value를 문자열 배열로 반환합니다. 404·빈 값은 [].
 */
export async function getProjectProfileSetting(
  projectCode: string,
  category: string,
  settingKey: string
): Promise<string[]> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
  const url = getApiV1Url(
    `/projects/${encodeURIComponent(projectCode)}/profiles/${encodeURIComponent(category)}/${encodeURIComponent(settingKey)}`
  );
  const response = await fetch(url, { method: 'GET', headers });
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw new Error(`프로필 항목 조회 실패: ${response.status}`);
  }
  const json: unknown = await response.json();
  const data = (json as { data?: unknown }).data ?? json;
  const d = data as {
    settingValue?: { value?: string[] | string };
    profile?: { settingValue?: { value?: string[] | string } };
  };
  const settingValue = d.settingValue ?? d.profile?.settingValue;
  if (!settingValue) {
    return [];
  }
  const raw = settingValue.value;
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return [raw];
  }
  return [];
}